import i18next from "i18next";
import { logger } from "../../util/Logger.js";
import {
  CONFIGURATOR_DATA,
  CONFIGURATOR_MODEL_TYPE,
  resolveSceneModulesWithFeatures,
  ResolvedModuleWithFeatures,
} from "../../core/configurator/ConfiguratorData.js";
import type { ProformaProduct } from "./SugarProforma.js";

import { MODULE_VARIANT_PRICE_URL } from "../../config/api.js";

interface ModulePriceResult {
  matched: boolean;
  variantId: string | null;
  sku: string | null;
  amount: number | null;
  resolvedCurrencyCode: string | null;
  priceLabel: string | null;
  matchScore: number;
}

export interface PriceCalculationResult {
  products: ProformaProduct[];
  totalPrice: number;
}

// ── Yardımcılar ──────────────────────────────────────────────────

interface HashableModel {
  data?: { id?: number };
  partMaterialGroups?: Array<{
    id?: number;
    material?: { id?: number };
  }>;
}

function buildSceneHash(models: HashableModel[][]): string {
  if (!models?.length) return "";
  return models
    .flat()
    .map((m) => {
      if (!m.data?.id || !m.partMaterialGroups?.length) return undefined;
      return m.partMaterialGroups
        .map((p) =>
          !p?.id ? "no-part" : `${m.data!.id}-${p.id}-${p.material?.id ?? "no-material"}`
        )
        .join(",");
    })
    .filter(Boolean)
    .sort()
    .join("|");
}

function findThumbnail(resolveKey: string, configData?: CONFIGURATOR_MODEL_TYPE): string {
  const firstId = Number(resolveKey.match(/^(\d+)/)?.[1]);
  if (!firstId) return "";
  const models = (configData ?? CONFIGURATOR_DATA).models;
  return models.find((m) => m.id === firstId)?.thumbnailFileUrl ?? "";
}

function parseDimensions(size: string) {
  const nums = size.replace(/\s*cm$/i, "").split("x").map(Number);
  return { width: nums[0] || 0, height: nums[1] || 0, depth: nums[2] || 0 };
}

function groupByModuleId(
  resolved: ResolvedModuleWithFeatures[],
  results: ModulePriceResult[]
) {
  const grouped = new Map<
    string,
    { mod: ResolvedModuleWithFeatures; result: ModulePriceResult; count: number }
  >();

  for (let i = 0; i < resolved.length; i++) {
    const mod = resolved[i];
    const existing = grouped.get(mod.id);
    if (existing) {
      existing.count++;
    } else {
      grouped.set(mod.id, { mod, result: results[i], count: 1 });
    }
  }

  return grouped;
}

function toProformaProducts(
  grouped: ReturnType<typeof groupByModuleId>,
  configData?: CONFIGURATOR_MODEL_TYPE,
): ProformaProduct[] {
  let idx = 0;
  logger.info("grouped", grouped);
  return Array.from(grouped.values()).map(({ mod, result, count }) => ({
    id: idx++,
    name: mod.module,
    price: result?.amount ?? 0,
    quantity: count,
    uuid: result?.variantId ?? `unmatched-${mod.id}`,
    configuratorModuleKind: mod.kind,
    options: Object.entries(mod.features).map(([name, value]) => ({
      optionName: name,
      optionValue: value,
    })),
    dimensions: parseDimensions(mod.features["Size"] ?? ""),
    thumbnail: findThumbnail(mod.key, configData),
    materialOptions: {
      groupId: 0,
      materialId: 0,
      groupName: mod.fabric,
      materialName: `${mod.fabric} - ${mod.color}`,
    },
  }));
}

// ── Calculator ───────────────────────────────────────────────────

export class ModulePriceCalculator {
  private _sceneHash = "";
  private _companyId: number;
  private _configData?: CONFIGURATOR_MODEL_TYPE;

  constructor(companyId?: number, configData?: CONFIGURATOR_MODEL_TYPE) {
    const params = new URLSearchParams(window.location.search);
    this._companyId = companyId ?? (Number(params.get("companyId")) || 8702);
    this._configData = configData;
  }

  /** Sahne değişti mi kontrol eder. */
  hasChanged(sceneModels: unknown[][]): boolean {
    const hash = buildSceneHash(sceneModels as HashableModel[][]);
    if (hash === this._sceneHash) return false;
    this._sceneHash = hash;
    return true;
  }

  /** Sahne modellerinden fiyat hesaplar. */
  async calculate(
    sceneModels: unknown[][],
    configData?: CONFIGURATOR_MODEL_TYPE,
  ): Promise<PriceCalculationResult> {
    const data = configData ?? this._configData;
    const resolved = resolveSceneModulesWithFeatures(sceneModels, data?.configurator?.moduleCatalog);

    if (resolved.length === 0) {
      return { products: [], totalPrice: 0 };
    }

    const body = {
      currency: i18next.t("__currency_code__"),
      companyId: this._companyId,
      modules: resolved.map((m) => ({
        module: m.id,
        fabric: m.fabric,
        color: m.color,
      })),
    };

    const response = await fetch(MODULE_VARIANT_PRICE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Fiyat API hatası: ${response.status}`);
    }

    const { results } = (await response.json()) as { results: ModulePriceResult[] };
    const grouped = groupByModuleId(resolved, results);
    const products = toProformaProducts(grouped, data);
    const totalPrice = products.reduce((sum, p) => sum + p.price * p.quantity, 0);

    return { products, totalPrice };
  }
}
